/**
 * Mirrors the backend contract documented in
 * `meander-backend/docs/RFID_TAP_TO_CHARGE_FRONTEND_CONTRACT.md`.
 *
 * The app only ever sees the safe shapes below; raw RFID UIDs never leave the
 * server. `last4_uid` is the only identifier exposed to the client.
 */

export type RfidCardStatus =
  | 'PENDING_CLAIM'
  | 'ACTIVE'
  | 'DISABLED'
  | 'REVOKED';

export type TapOutcome =
  | 'UNKNOWN_TAG'
  | 'MATCHED_PENDING_CLAIM'
  | 'AUTHORIZED'
  | 'DENIED_NO_PM'
  | 'DENIED_DISABLED'
  | 'DENIED_NO_ACCESS'
  | 'DENIED_INTERNAL_ERROR'
  | 'DENIED_CHARGER_BLOCKED';

export interface ApiRfidCard {
  uuid: string;
  nickname: string | null;
  last4_uid: string | null;
  status: RfidCardStatus;
  auto_charge_enabled: boolean;
  last_used_at: string | null;
  last_used_charger_uuid: string | null;
  created_at: string;
}

export interface TapEvent {
  created_at: string;
  charger_uuid: string;
  outcome: TapOutcome;
  reason: string | null;
  order_uuid: string | null;
}

export interface PairingStartResponse {
  card_uuid: string;
  claim_token: string;
  expires_at: string;
}

export interface PairingCancelResponse {
  cancelled: boolean;
}

export interface RfidCardsListResponse {
  cards: ApiRfidCard[];
}

export interface RfidCardResponse {
  card: ApiRfidCard;
}

export interface RfidTapsResponse {
  taps: TapEvent[];
}

export const formatCardLabel = (card: ApiRfidCard): string => {
  if (card.nickname && card.nickname.trim().length > 0) return card.nickname;
  if (card.last4_uid) return `Card ••${card.last4_uid}`;
  return 'Unnamed card';
};

export const STATUS_LABELS: Record<RfidCardStatus, string> = {
  PENDING_CLAIM: 'Awaiting tap',
  ACTIVE: 'Active',
  DISABLED: 'Disabled',
  REVOKED: 'Revoked',
};

export const STATUS_COLORS: Record<RfidCardStatus, string> = {
  PENDING_CLAIM: '#F59E0B',
  ACTIVE: '#10B981',
  DISABLED: '#8D8E90',
  REVOKED: '#EF4444',
};

export const OUTCOME_LABELS: Record<TapOutcome, string> = {
  UNKNOWN_TAG: 'Unrecognized tag',
  MATCHED_PENDING_CLAIM: 'Tag paired',
  AUTHORIZED: 'Charging started',
  DENIED_NO_PM: 'No payment method',
  DENIED_DISABLED: 'Tag disabled',
  DENIED_NO_ACCESS: 'No access',
  DENIED_INTERNAL_ERROR: 'Internal error',
  DENIED_CHARGER_BLOCKED: 'Charger blocked',
};

export const OUTCOME_COLORS: Record<TapOutcome, string> = {
  UNKNOWN_TAG: '#8D8E90',
  MATCHED_PENDING_CLAIM: '#40BFB4',
  AUTHORIZED: '#10B981',
  DENIED_NO_PM: '#F59E0B',
  DENIED_DISABLED: '#8D8E90',
  DENIED_NO_ACCESS: '#EF4444',
  DENIED_INTERNAL_ERROR: '#EF4444',
  DENIED_CHARGER_BLOCKED: '#EF4444',
};

/**
 * Lightweight relative-time formatter used in the RFID list/detail UI to
 * surface `last_used_at` without pulling in a date library.
 */
export const formatRelativeTime = (iso: string | null | undefined): string => {
  if (!iso) return 'Never';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'Never';
  const diffMs = Date.now() - then;
  if (diffMs < 0) return 'Just now';
  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'Just now';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} minute${min === 1 ? '' : 's'} ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr === 1 ? '' : 's'} ago`;
  const day = Math.floor(hr / 24);
  if (day < 7) return `${day} day${day === 1 ? '' : 's'} ago`;
  const wk = Math.floor(day / 7);
  if (wk < 4) return `${wk} week${wk === 1 ? '' : 's'} ago`;
  const mo = Math.floor(day / 30);
  if (mo < 12) return `${mo} month${mo === 1 ? '' : 's'} ago`;
  const yr = Math.floor(day / 365);
  return `${yr} year${yr === 1 ? '' : 's'} ago`;
};
